import React, { useEffect, useRef } from 'react';
import { Copy, Trash2, Clipboard } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';

interface ElementContextMenuProps {
    isOpen: boolean;
    position: { x: number; y: number };
    onClose: () => void;
    onDelete: () => void;
    onCopy: () => void;
    onPaste?: () => void;
    canPaste: boolean;
}

export default function ElementContextMenu({
    isOpen,
    position,
    onClose,
    onDelete,
    onCopy,
    onPaste,
    canPaste,
}: ElementContextMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleMenuItemClick = (action: () => void) => {
        action();
        onClose();
    };

    const CONTEXT_MENU_OFFSET_X = 320;
    const CONTEXT_MENU_OFFSET_Y = 195;

    return (
        <div
            ref={menuRef}
            className="absolute z-50 bg-background border border-border rounded-md shadow-lg py-1 min-w-[140px]"
            style={{
                left: position.x - CONTEXT_MENU_OFFSET_X,
                top: position.y - CONTEXT_MENU_OFFSET_Y,
                position: 'fixed', // Override the className to ensure fixed positioning
            }}
        >
            <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMenuItemClick(onCopy)}
                className="w-full justify-start"
            >
                <Copy className="w-4 h-4" />
                Copy
            </Button>

            {canPaste && onPaste && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMenuItemClick(onPaste)}
                    className="w-full justify-start"
                >
                    <Clipboard className="w-4 h-4" />
                    Paste
                </Button>
            )}

            <hr className="my-1 border-border" />

            <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMenuItemClick(onDelete)}
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            >
                <Trash2 className="w-4 h-4" />
                Delete
            </Button>
        </div>
    );
}
